'use client';

import React, { useState, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { X, GripVertical, Check, RotateCcw, Eye, EyeOff, Loader2 } from 'lucide-react';
import { cn } from '../lib/utils';

// ─── Types ──────────────────────────────────────────────────

export interface CustomisePanelItem {
  key: string;
  label: string;
  description?: string;
  locked?: boolean;
}

export interface CustomisePanelProps {
  open: boolean;
  onClose: () => void;
  title?: string;
  description?: string;
  items: CustomisePanelItem[];
  visibleKeys: Record<string, boolean>;
  keyOrder: string[];
  onToggleVisibility: (key: string) => void;
  onReorder: (newOrder: string[]) => void;
  onReset?: () => void;
  isCustomised?: boolean;
  isSaving?: boolean;
  extraFooter?: React.ReactNode;
}

// ─── Component ──────────────────────────────────────────────

export function CustomisePanel({
  open,
  onClose,
  title = 'Customise View',
  description = 'Drag to reorder. Toggle items to show or hide them on this page.',
  items,
  visibleKeys,
  keyOrder,
  onToggleVisibility,
  onReorder,
  onReset,
  isCustomised = false,
  isSaving = false,
  extraFooter,
}: CustomisePanelProps) {
  const [draggedKey, setDraggedKey] = useState<string | null>(null);
  const [dragOverKey, setDragOverKey] = useState<string | null>(null);
  const listRef = useRef<HTMLDivElement>(null);

  // Build ordered items list
  const orderedItems = keyOrder
    .map((key) => items.find((i) => i.key === key))
    .filter(Boolean) as CustomisePanelItem[];

  // Add any items not in keyOrder (new items)
  const missingItems = items.filter((i) => !keyOrder.includes(i.key));
  const allOrderedItems = [...orderedItems, ...missingItems];

  const visibleCount = allOrderedItems.filter(
    (item) => item.locked || visibleKeys[item.key] !== false,
  ).length;

  // ─── Drag and Drop ────────────────────────────────────────

  const handleDragStart = useCallback((e: React.DragEvent, key: string) => {
    setDraggedKey(key);
    e.dataTransfer.effectAllowed = 'move';
    // Make the drag image slightly transparent
    if (e.currentTarget instanceof HTMLElement) {
      e.dataTransfer.setDragImage(e.currentTarget, 0, 0);
    }
  }, []);

  const handleDragOver = useCallback(
    (e: React.DragEvent, key: string) => {
      e.preventDefault();
      e.dataTransfer.dropEffect = 'move';
      if (key !== draggedKey) {
        setDragOverKey(key);
      }
    },
    [draggedKey],
  );

  const handleDrop = useCallback(
    (e: React.DragEvent, dropKey: string) => {
      e.preventDefault();
      if (!draggedKey || draggedKey === dropKey) return;

      const currentOrder = allOrderedItems.map((i) => i.key);
      const dragIdx = currentOrder.indexOf(draggedKey);
      const dropIdx = currentOrder.indexOf(dropKey);
      if (dragIdx === -1 || dropIdx === -1) return;

      const newOrder = [...currentOrder];
      newOrder.splice(dragIdx, 1);
      newOrder.splice(dropIdx, 0, draggedKey);
      onReorder(newOrder);

      setDraggedKey(null);
      setDragOverKey(null);
    },
    [draggedKey, allOrderedItems, onReorder],
  );

  const handleDragEnd = useCallback(() => {
    setDraggedKey(null);
    setDragOverKey(null);
  }, []);

  if (!open) return null;
  if (typeof window === 'undefined') return null;

  const content = (
    <div className="fixed inset-0 z-50 flex justify-end">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/30 backdrop-blur-sm"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Side panel */}
      <div className="relative z-10 flex h-full w-full max-w-md flex-col bg-white shadow-2xl animate-in slide-in-from-right duration-200">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-slate-200 px-6 py-4">
          <div>
            <h2 className="font-heading text-card-title text-text-heading">{title}</h2>
            <p className="mt-0.5 font-body text-caption text-text-muted">
              {visibleCount} of {allOrderedItems.length} items visible
            </p>
          </div>
          <div className="flex items-center gap-2">
            {isSaving && (
              <span className="inline-flex items-center gap-1 font-body text-caption text-text-subtle">
                <Loader2 className="h-3 w-3 animate-spin" />
                Saving
              </span>
            )}
            <button
              type="button"
              onClick={onClose}
              className="inline-flex h-8 w-8 items-center justify-center rounded-lg text-text-subtle transition-interactive duration-200 hover:bg-slate-100 hover:text-text-body"
              aria-label="Close"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>

        {/* Item list */}
        <div ref={listRef} className="flex-1 overflow-y-auto px-4 py-3">
          <p className="mb-3 font-body text-caption text-text-muted">{description}</p>

          <div className="space-y-1">
            {allOrderedItems.map((item) => {
              const isLocked = item.locked === true;
              const isVisible = isLocked || visibleKeys[item.key] !== false;
              const isDragging = draggedKey === item.key;
              const isDragOver = dragOverKey === item.key;

              return (
                <div
                  key={item.key}
                  draggable
                  onDragStart={(e) => handleDragStart(e, item.key)}
                  onDragOver={(e) => handleDragOver(e, item.key)}
                  onDrop={(e) => handleDrop(e, item.key)}
                  onDragEnd={handleDragEnd}
                  className={cn(
                    'flex items-center gap-3 rounded-xl border px-3 py-2.5 transition-interactive duration-200',
                    isVisible
                      ? 'border-slate-200 bg-white'
                      : 'border-slate-100 bg-slate-50 opacity-60',
                    isDragging && 'opacity-40 scale-95',
                    isDragOver &&
                      'border-statura-green-accessible ring-1 ring-statura-green-accessible/20',
                    'cursor-grab active:cursor-grabbing',
                  )}
                >
                  {/* Drag handle */}
                  <GripVertical className="h-4 w-4 shrink-0 text-slate-300" />

                  {/* Label */}
                  <div className="flex-1 min-w-0">
                    <span
                      className={cn(
                        'font-body text-body-sm font-medium',
                        isVisible ? 'text-text-heading' : 'text-text-subtle',
                      )}
                    >
                      {item.label}
                    </span>
                    {item.description && (
                      <p className="truncate font-body text-caption text-text-subtle">
                        {item.description}
                      </p>
                    )}
                    {isLocked && (
                      <p className="font-body text-caption text-text-subtle">
                        Required workflow item
                      </p>
                    )}
                  </div>

                  {/* Toggle button */}
                  <button
                    type="button"
                    disabled={isLocked}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (isLocked) return;
                      onToggleVisibility(item.key);
                    }}
                    className={cn(
                      'inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-lg transition-interactive duration-200',
                      isLocked
                        ? 'cursor-not-allowed bg-slate-100 text-slate-300'
                        : isVisible
                          ? 'bg-statura-green-accessible/10 text-statura-green-accessible hover:bg-statura-green-accessible/20'
                          : 'bg-slate-100 text-slate-400 hover:bg-slate-200',
                    )}
                    title={isLocked ? 'Required' : isVisible ? 'Hide' : 'Show'}
                  >
                    {isVisible ? (
                      <Eye className="h-3.5 w-3.5" />
                    ) : (
                      <EyeOff className="h-3.5 w-3.5" />
                    )}
                  </button>
                </div>
              );
            })}
          </div>
        </div>

        {/* Footer */}
        <div className="border-t border-slate-200 bg-surface-soft">
          {extraFooter ? (
            <div className="border-b border-slate-200 px-6 py-3">{extraFooter}</div>
          ) : null}
          <div className="flex items-center justify-between px-6 py-3">
            {onReset && isCustomised ? (
              <button
                type="button"
                onClick={onReset}
                className="inline-flex items-center gap-1.5 font-body text-caption font-medium text-text-body transition-interactive duration-200 hover:text-text-heading"
              >
                <RotateCcw className="h-3.5 w-3.5" />
                Reset to defaults
              </button>
            ) : (
              <span className="font-body text-caption text-text-subtle">
                {isCustomised ? 'Custom layout' : 'Default layout'}
              </span>
            )}

            <button
              type="button"
              onClick={onClose}
              className="inline-flex items-center gap-1.5 rounded-lg bg-statura-ink-dark px-4 py-2 font-body text-caption font-semibold text-white transition-interactive duration-200 hover:bg-statura-ink"
            >
              <Check className="h-3.5 w-3.5" />
              Done
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  return createPortal(content, document.body);
}
